import React from 'react';
import { max } from 'date-fns';
import { AnalysisPage } from './AnalysisPage';
import { getSiteContext } from '../../../state/contexts/site';
import {
  getAnalysisPageContext,
  setStartDate,
  setEndDate,
  parseAnalysisRouteContext,
  setContext,
  validateAnalysisContext,
  updateViews,
  updateTagsForChartInView,
  View,
  removeTagFromChartInView,
  removeChartInView,
} from '../../../state/contexts/analysisPage';
import { fetchSnapshots, getFullNumericDataSet, getNumericDataSet } from '../../../state/entities';
import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../state';
import { isPending } from '../../../state/request';
import { ActionType } from 'typesafe-actions';
import { useAction } from '../../../helpers/hooks';
import { useLocation } from 'react-router-dom';
import { getHistory } from '../../../state';
import { PathBuilder } from '../../../routing';

const getDefaultRouteContext = () => ({
  start: new Date(Date.now() - 1000 * 60 * 60 * 48),
  end: new Date(Date.now()),
  tagIds: [],
  views: [],
});

const getTagIdsFromViews = (views: View[]) => {
  return views.reduce<string[]>(
    (acc, view) => [
      ...acc,
      ...view.charts.reduce<string[]>((chartAcc, chart) => [...chartAcc, ...chart.tagsIds], []),
    ],
    []
  );
};

export const AnalysisPageLoader: React.FC<{}> = () => {
  const site = useSelector(getSiteContext);
  const { id: siteId } = site;
  const context = useSelector(getAnalysisPageContext);

  const [routeError, setRouteError] = useState<string | undefined>();

  const doSetStartDate = useAction(setStartDate);
  const doSetEndDate = useAction(setEndDate);
  const doSetContext = useAction(setContext);
  const doReset = () => {
    setRouteError(undefined);
    const history = getHistory();
    history.push(PathBuilder.ANALYSIS(siteId));
    doSetContext(getDefaultRouteContext());
  };
  const doupdateViews = useAction(updateViews);
  const doUpdateTagsForChartInView = useAction(updateTagsForChartInView);
  const doRemoveTagFromChartInView = useAction(removeTagFromChartInView);
  const doRemoveChartInView = useAction(removeChartInView);
  const dispatch = useDispatch();
  const location = useLocation();

  useEffect(() => {
    if (context.views.length > 0) {
      setRouteError(undefined);
    }
  }, [context]);

  useEffect(() => {
    try {
      const routeContext = parseAnalysisRouteContext(location.pathname, location.search);
      validateAnalysisContext(routeContext, site);
      if (!routeContext.start && !routeContext.end) {
        routeContext.start = new Date(Date.now() - 1000 * 60 * 60 * 48);
        routeContext.end = new Date(Date.now());
      }
      doSetContext(routeContext);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (e: any) {
      // If any error is encountered parsing the route context
      doSetContext(getDefaultRouteContext());
      setRouteError(e.message);
    }
    // todo: add useEffectOnce hook?
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const lastRequest = useRef<ActionType<typeof fetchSnapshots.request> | undefined>(undefined);

  useEffect(() => {
    const { start, end, views } = context;
    if (start && end && views.length > 0 && !routeError) {
      const tagIds = getTagIdsFromViews(views);
      const action = fetchSnapshots.request(siteId, tagIds, start, end, true);
      lastRequest.current = action;
      dispatch(action);
    }
  }, [context, dispatch, routeError, siteId]);

  // get snapshots
  const snapshots = useSelector((state: RootState) => {
    if (context.start && context.end && context.views.length > 0 && !routeError) {
      const tagIds = getTagIdsFromViews(context.views);
      return getNumericDataSet(state, tagIds, context.start, context.end);
    } else {
      return {};
    }
  });

  const lastUpdated = Object.values(snapshots).reduce((acc, curr) => {
    const lastPoint = curr.points[curr.points.length - 1];
    return lastPoint && lastPoint.time ? (acc ? max([acc, lastPoint.time]) : lastPoint.time) : acc;
  }, null as Date | null);

  // TODO: remove this once we have a better way to handle this
  // for bitfield and enum tags we need to get the full range of data
  // this is a temporary solution until we have a better way to handle this
  const fullRangeSnapshots = useSelector((state: RootState) => {
    if (context.start && context.end && context.views.length > 0 && !routeError) {
      const tagIds = getTagIdsFromViews(context.views);
      return getFullNumericDataSet(state, tagIds, context.start, context.end);
    } else {
      return {};
    }
  });

  const loading = useSelector(
    (state: RootState) =>
      Boolean(lastRequest.current && isPending(state, lastRequest.current)) ||
      Boolean(context.start === undefined || context.end === undefined)
  );

  return (
    <AnalysisPage
      site={site}
      snapshots={snapshots}
      fullRangeSnapshots={fullRangeSnapshots}
      start={context.start!}
      end={context.end!}
      views={context.views}
      setStartDate={doSetStartDate}
      setEndDate={doSetEndDate}
      updateViews={doupdateViews}
      updateTagsForChartInView={doUpdateTagsForChartInView}
      removeTagFromChartInView={doRemoveTagFromChartInView}
      removeChartInView={doRemoveChartInView}
      reset={doReset}
      error={routeError}
      loading={loading}
      lastUpdated={lastUpdated}
    />
  );
};
